import { looksLikeHttpUrl, normalizeHttpUrl, normalizeValidHttpUrl } from '@/lib/utils/url';
import {
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
} from '@/lib/domain/question-items/pollOptionConstraints';
import type { QuizScheduledGenerationInput } from './types';
import {
  parseQuizQuestionSlots,
  parseQuizQuestionSlotOutcomes,
  type QuizQuestionSlot,
  type QuizQuestionSlotOutcome,
} from '../../../../../supabase/functions/_shared/quizQuestionPlan';
export {
  MAX_QUIZ_QUESTION_SLOTS,
  parseQuizQuestionSlots,
  parseQuizQuestionSource,
  parseQuizQuestionSlotOutcomes,
  RSS_MATCH_FIELDS,
  KIT_MATCH_FIELDS,
  type QuizQuestionSlot,
  type QuizQuestionSlotItemType,
  type QuizQuestionSource,
  type QuizQuestionSlotOutcome,
  type RssArticleMatchingFilter,
  type KitPostMatchingFilter,
} from '../../../../../supabase/functions/_shared/quizQuestionPlan';

/** V1 source kinds supported by QuizSource. Upload/file is deferred. */
export type QuizSourceKind =
  | 'pasted_content'
  | 'article_url'
  | 'rss_feed'
  | 'kit_emails'
  | 'topic_custom'
  | 'question_library';

/** Who owns refreshing source content; cadence remains a separate RefreshPolicy concern. */
export type QuizSourceRefreshCapability = 'manual' | 'feed_driven' | 'managed';

/** Generation surface that created an edition when lifecycle behavior depends on its handoff. */
export type QuizGenerationOrigin = 'integrator_api';

export const MIN_POLL_OPTION_COUNT = POLL_MIN_OPTIONS;
export const MAX_POLL_OPTION_COUNT = POLL_MAX_OPTIONS;
export const DEFAULT_POLL_OPTION_COUNT = 4;

export function normalizePollOptionCount(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_POLL_OPTION_COUNT;
  }
  return Math.min(MAX_POLL_OPTION_COUNT, Math.max(MIN_POLL_OPTION_COUNT, Math.trunc(value)));
}

export interface PastedContentInput {
  character_count: string;
}

export interface ArticleUrlInput {
  url: string;
  title?: string;
}

export interface RssFeedInput {
  feed_url: string;
  item_url?: string;
  item_title?: string;
}

export interface KitEmailsInput {
  mode: 'recent' | 'specific';
  post_scope: 'email' | 'all';
  post_ids: string[];
}

export interface TopicCustomInput {
  prompt: string;
}

export interface QuestionLibraryInput {
  /** Empty means the full Atrivial question library. */
  category_slugs: string[];
  /** Desired questions per scheduled edition. Missing legacy values default to four. */
  question_count?: string;
}

export type QuizSourceInputByKind = {
  pasted_content: PastedContentInput;
  article_url: ArticleUrlInput;
  rss_feed: RssFeedInput;
  kit_emails: KitEmailsInput;
  topic_custom: TopicCustomInput;
  question_library: QuestionLibraryInput;
};

export interface QuizSourceConfigInput {
  [key: string]: string | string[] | undefined;
  character_count?: string;
  url?: string;
  title?: string;
  feed_url?: string;
  item_url?: string;
  item_title?: string;
  mode?: string;
  post_scope?: string;
  post_ids?: string[];
  prompt?: string;
  category_slugs?: string[];
  question_count?: string;
}

export interface QuizSourceConfig {
  kind: QuizSourceKind;
  /** Normalized partner-editable source payload (URL, feed URL, topic text, etc.). */
  input: QuizSourceConfigInput;
  /** Partner-editable defaults used the next time this Quiz generates questions. */
  generationSettings?: Record<string, unknown>;
}

/** Immutable metadata captured when an edition is generated. */
export interface QuizSourceSnapshot {
  kind: QuizSourceKind;
  config: QuizSourceConfig;
  capturedAt: string;
  /** Raw partner-entered source text retained so workspace inputs can be prefilled later. */
  inputText?: string;
  /** Ordered question-type intent captured before generation starts. */
  questionSlots?: QuizQuestionSlot[];
  /** Per-position source resolution, including planned questions omitted from this edition. */
  questionSlotOutcomes?: QuizQuestionSlotOutcome[];
  /** Present when another product surface deliberately hands the edition to the Portal. */
  generationOrigin?: QuizGenerationOrigin;
}

export function isIntegratorApiSourceSnapshot(
  snapshot: QuizSourceSnapshot | null | undefined
): boolean {
  return snapshot?.generationOrigin === 'integrator_api';
}

export interface QuizSourceInputContext {
  /** Article title from generation/scrape context. */
  title?: string;
  /** Selected RSS item URL. */
  itemUrl?: string;
  /** Selected RSS item title. */
  itemTitle?: string;
}

export const QUIZ_SOURCE_KINDS: readonly QuizSourceKind[] = [
  'pasted_content',
  'article_url',
  'rss_feed',
  'kit_emails',
  'topic_custom',
  'question_library',
] as const;

/** Text longer than this, or multi-line, is treated as pasted content rather than a topic prompt. */
export const PASTED_CONTENT_CHARACTER_THRESHOLD = 200;

const QUIZ_SOURCE_KIND_SET = new Set<string>(QUIZ_SOURCE_KINDS);

function isQuizSourceKind(value: unknown): value is QuizSourceKind {
  return typeof value === 'string' && QUIZ_SOURCE_KIND_SET.has(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
  if (!isRecord(value)) return false;
  return Object.values(value).every((entry) => typeof entry === 'string');
}

function parseLegacyApiInputText(value: unknown): string | null {
  if (!isRecord(value) || !isRecord(value.input) || value.input.kind !== 'text') {
    return null;
  }
  const inputText = typeof value.input.text === 'string' ? value.input.text.trim() : '';
  return inputText || null;
}

function looksLikeRssUrl(value: string): boolean {
  const normalized = normalizeHttpUrl(value).toLowerCase();
  return (
    looksLikeHttpUrl(normalized) &&
    (normalized.includes('/rss') ||
      normalized.includes('/feed') ||
      normalized.endsWith('.xml') ||
      normalized.includes('format=rss'))
  );
}

function extractHttpUrlTokens(inputText: string): string[] {
  return inputText
    .trim()
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => normalizeValidHttpUrl(value))
    .filter((value): value is string => value !== null);
}

function looksLikePastedContent(inputText: string): boolean {
  const trimmed = inputText.trim();
  if (!trimmed) return false;

  if (trimmed.length >= PASTED_CONTENT_CHARACTER_THRESHOLD) {
    return true;
  }

  const lines = trimmed.split(/\r?\n/).filter((line) => line.trim().length > 0);
  return lines.length > 1;
}

export function detectQuizSourceKind(inputText: string): QuizSourceKind {
  const trimmed = inputText.trim();
  if (!trimmed) {
    return 'topic_custom';
  }

  const tokens = trimmed.split(/[\s,]+/).filter(Boolean);
  if (tokens.length > 0 && tokens.every((token) => looksLikeHttpUrl(token))) {
    if (tokens.length === 1 && looksLikeRssUrl(tokens[0])) {
      return 'rss_feed';
    }

    return 'article_url';
  }

  return looksLikePastedContent(trimmed) ? 'pasted_content' : 'topic_custom';
}

export function recommendRefreshCapability(kind: QuizSourceKind): QuizSourceRefreshCapability {
  switch (kind) {
    case 'rss_feed':
    case 'kit_emails':
      return 'feed_driven';
    case 'question_library':
      return 'managed';
    case 'pasted_content':
    case 'article_url':
    case 'topic_custom':
    default:
      return 'manual';
  }
}

/** Managed sources replace their Quiz Edition content automatically. */
export function quizSourceAllowsManualQuestionEditing(
  sourceConfig: QuizSourceConfig | null | undefined
): boolean {
  return sourceConfig?.kind !== 'question_library';
}

/** True when a saved Quiz Source contains enough input to drive Content and future generation. */
export function quizSourceHasUsableInput(
  sourceConfig: QuizSourceConfig | null | undefined
): boolean {
  if (!sourceConfig) return false;

  switch (sourceConfig.kind) {
    case 'pasted_content': {
      const characterCount = Number(sourceConfig.input.character_count);
      return Number.isFinite(characterCount) && characterCount > 0;
    }
    case 'article_url':
      return Boolean(sourceConfig.input.url?.trim());
    case 'rss_feed':
      return Boolean(sourceConfig.input.feed_url?.trim());
    case 'kit_emails':
      return (
        sourceConfig.input.mode === 'recent' ||
        (sourceConfig.input.mode === 'specific' && Boolean(sourceConfig.input.post_ids?.length))
      );
    case 'topic_custom':
      return Boolean(sourceConfig.input.prompt?.trim());
    case 'question_library':
      return true;
  }
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizePastedContentInput(input: PastedContentInput): PastedContentInput {
  return { character_count: input.character_count.trim() };
}

function normalizeArticleUrlInput(input: ArticleUrlInput): ArticleUrlInput {
  const url = normalizeValidHttpUrl(input.url.trim()) ?? input.url.trim();
  const normalized: ArticleUrlInput = { url };
  const title = normalizeOptionalString(input.title);
  if (title) normalized.title = title;
  return normalized;
}

function normalizeRssFeedInput(input: RssFeedInput): RssFeedInput {
  const normalized: RssFeedInput = {
    feed_url: normalizeHttpUrl(input.feed_url.trim()),
  };
  const itemUrl = input.item_url ? normalizeValidHttpUrl(input.item_url.trim()) : undefined;
  if (itemUrl) normalized.item_url = itemUrl;
  const itemTitle = normalizeOptionalString(input.item_title);
  if (itemTitle) normalized.item_title = itemTitle;
  return normalized;
}

function normalizeKitEmailsInput(input: KitEmailsInput): KitEmailsInput {
  return {
    mode: input.mode,
    post_scope: input.post_scope,
    post_ids: [...new Set(input.post_ids.map((postId) => postId.trim()).filter(Boolean))],
  };
}

function normalizeTopicCustomInput(input: TopicCustomInput): TopicCustomInput {
  return { prompt: input.prompt.trim() };
}

function normalizeQuestionLibraryInput(input: QuestionLibraryInput): QuestionLibraryInput {
  const categorySlugs = [
    ...new Set(input.category_slugs.map((slug) => slug.trim()).filter(Boolean)),
  ];
  const normalized = { category_slugs: categorySlugs };
  const questionCount = normalizeOptionalString(input.question_count);
  return questionCount ? { ...normalized, question_count: questionCount } : normalized;
}

function normalizeInputForKind<K extends QuizSourceKind>(
  kind: K,
  input: QuizSourceInputByKind[K]
): QuizSourceConfigInput {
  switch (kind) {
    case 'pasted_content':
      return { ...normalizePastedContentInput(input as PastedContentInput) };
    case 'article_url':
      return { ...normalizeArticleUrlInput(input as ArticleUrlInput) };
    case 'rss_feed':
      return { ...normalizeRssFeedInput(input as RssFeedInput) };
    case 'kit_emails':
      return { ...normalizeKitEmailsInput(input as KitEmailsInput) };
    case 'topic_custom':
      return { ...normalizeTopicCustomInput(input as TopicCustomInput) };
    case 'question_library':
      return { ...normalizeQuestionLibraryInput(input as QuestionLibraryInput) };
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function buildQuizSourceConfig<K extends QuizSourceKind>(
  kind: K,
  input: QuizSourceInputByKind[K]
): QuizSourceConfig {
  return {
    kind,
    input: normalizeInputForKind(kind, input),
  };
}

export function buildQuizSourceSnapshot<K extends QuizSourceKind>(
  kind: K,
  input: QuizSourceInputByKind[K],
  capturedAt: string = new Date().toISOString()
): QuizSourceSnapshot {
  return {
    kind,
    config: buildQuizSourceConfig(kind, input),
    capturedAt,
  };
}

export function buildQuizSourceSnapshotFromText(
  kind: QuizSourceKind,
  inputText: string,
  capturedAt: string = new Date().toISOString()
): QuizSourceSnapshot {
  const snapshot = (() => {
    switch (kind) {
      case 'pasted_content':
        return buildQuizSourceSnapshot(kind, deriveQuizSourceInput(kind, inputText), capturedAt);
      case 'article_url':
        return buildQuizSourceSnapshot(kind, deriveQuizSourceInput(kind, inputText), capturedAt);
      case 'rss_feed':
        return buildQuizSourceSnapshot(kind, deriveQuizSourceInput(kind, inputText), capturedAt);
      case 'kit_emails':
        return buildQuizSourceSnapshot(kind, deriveQuizSourceInput(kind, inputText), capturedAt);
      case 'topic_custom':
        return buildQuizSourceSnapshot(kind, deriveQuizSourceInput(kind, inputText), capturedAt);
      case 'question_library':
        return buildQuizSourceSnapshot(kind, deriveQuizSourceInput(kind, inputText), capturedAt);
      default: {
        const _exhaustive: never = kind;
        return _exhaustive;
      }
    }
  })();

  return { ...snapshot, inputText };
}

export function deriveQuizSourceInput<K extends QuizSourceKind>(
  kind: K,
  inputText: string,
  context: QuizSourceInputContext = {}
): QuizSourceInputByKind[K] {
  const trimmed = inputText.trim();

  switch (kind) {
    case 'pasted_content':
      return { character_count: String(trimmed.length) } as QuizSourceInputByKind[K];
    case 'article_url': {
      const url = extractHttpUrlTokens(trimmed)[0] ?? trimmed;
      const derived: ArticleUrlInput = { url };
      const title = normalizeOptionalString(context.title);
      if (title) derived.title = title;
      return derived as QuizSourceInputByKind[K];
    }
    case 'rss_feed': {
      const feedUrl = extractHttpUrlTokens(trimmed)[0] ?? normalizeHttpUrl(trimmed);
      const derived: RssFeedInput = { feed_url: feedUrl };
      const itemUrl = context.itemUrl ? normalizeValidHttpUrl(context.itemUrl.trim()) : undefined;
      if (itemUrl) derived.item_url = itemUrl;
      const itemTitle = normalizeOptionalString(context.itemTitle);
      if (itemTitle) derived.item_title = itemTitle;
      return derived as QuizSourceInputByKind[K];
    }
    case 'kit_emails': {
      const postIds = [
        ...new Set(
          trimmed
            .split(/[\s,]+/)
            .map((postId) => postId.trim())
            .filter(Boolean)
        ),
      ];
      return {
        mode: postIds.length > 0 ? 'specific' : 'recent',
        post_scope: 'email',
        post_ids: postIds,
      } as QuizSourceInputByKind[K];
    }
    case 'topic_custom':
      return { prompt: trimmed } as QuizSourceInputByKind[K];
    case 'question_library':
      return { category_slugs: trimmed ? [trimmed] : [] } as QuizSourceInputByKind[K];
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function validateInputForKind(kind: QuizSourceKind, input: unknown): boolean {
  if (kind === 'kit_emails') {
    return (
      isRecord(input) &&
      (input.mode === 'recent' || input.mode === 'specific') &&
      (input.post_scope === 'email' || input.post_scope === 'all') &&
      Array.isArray(input.post_ids) &&
      input.post_ids.every((postId) => typeof postId === 'string' && Boolean(postId.trim())) &&
      (input.mode === 'recent' || input.post_ids.length > 0)
    );
  }
  if (kind === 'question_library') {
    return (
      isRecord(input) &&
      Array.isArray(input.category_slugs) &&
      input.category_slugs.every((slug) => typeof slug === 'string' && Boolean(slug.trim())) &&
      (input.question_count === undefined || typeof input.question_count === 'string')
    );
  }
  if (!isStringRecord(input)) return false;
  if (kind === 'pasted_content') {
    const characterCount = Number(input.character_count);
    return (
      Number.isFinite(characterCount) &&
      characterCount > 0 &&
      !('source_text' in input) &&
      !('body' in input) &&
      !('content' in input)
    );
  }
  if (kind === 'article_url') return Boolean(input.url?.trim());
  if (kind === 'rss_feed') return Boolean(input.feed_url?.trim());
  return Boolean(input.prompt?.trim());
}

function parseQuestionLibraryInput(value: unknown): QuestionLibraryInput | null {
  if (!isRecord(value)) return null;
  const legacyCategorySlug =
    typeof value.category_slug === 'string' ? value.category_slug.trim() : '';
  const rawCategorySlugs =
    value.category_slugs === undefined
      ? legacyCategorySlug
        ? [legacyCategorySlug]
        : []
      : value.category_slugs;
  if (
    !Array.isArray(rawCategorySlugs) ||
    !rawCategorySlugs.every((slug) => typeof slug === 'string' && Boolean(slug.trim())) ||
    (value.question_count !== undefined && typeof value.question_count !== 'string')
  ) {
    return null;
  }
  return normalizeQuestionLibraryInput({
    category_slugs: rawCategorySlugs,
    ...(typeof value.question_count === 'string' ? { question_count: value.question_count } : {}),
  });
}

function parseKitEmailsInput(value: unknown): KitEmailsInput | null {
  if (
    !isRecord(value) ||
    (value.mode !== 'recent' && value.mode !== 'specific') ||
    (value.post_scope !== 'email' && value.post_scope !== 'all')
  ) {
    return null;
  }
  const rawPostIds =
    typeof value.post_ids === 'string'
      ? value.post_ids.split(',')
      : value.post_ids === undefined
        ? []
        : value.post_ids;
  if (!Array.isArray(rawPostIds) || !rawPostIds.every((postId) => typeof postId === 'string')) {
    return null;
  }
  const input = normalizeKitEmailsInput({
    mode: value.mode,
    post_scope: value.post_scope,
    post_ids: rawPostIds,
  });
  return validateInputForKind('kit_emails', input) ? input : null;
}

export function parseQuizSourceConfig(value: unknown): QuizSourceConfig | null {
  if (!isRecord(value)) return null;
  const legacyApiInputText = parseLegacyApiInputText(value);
  if (legacyApiInputText) {
    const kind = detectQuizSourceKind(legacyApiInputText);
    return buildQuizSourceSnapshotFromText(kind, legacyApiInputText).config;
  }
  if (!isQuizSourceKind(value.kind)) return null;
  const input =
    value.kind === 'question_library'
      ? parseQuestionLibraryInput(value.input)
      : value.kind === 'kit_emails'
        ? parseKitEmailsInput(value.input)
        : isStringRecord(value.input)
          ? value.input
          : null;
  if (!input || !validateInputForKind(value.kind, input)) return null;

  const generationSettings =
    isRecord(value.generationSettings) && !Array.isArray(value.generationSettings)
      ? value.generationSettings
      : undefined;

  return {
    kind: value.kind,
    input,
    ...(generationSettings ? { generationSettings } : {}),
  } as QuizSourceConfig;
}

export function parseQuizScheduledGenerationInput(
  value: unknown
): QuizScheduledGenerationInput | null {
  if (!isRecord(value)) return null;

  const sourceType = value.sourceType;
  if (sourceType !== 'partner_rss_feed' && sourceType !== 'partner_url_document') {
    return null;
  }
  if (!isRecord(value.sourceConfig) || !isRecord(value.generationConfig)) {
    return null;
  }

  return {
    sourceType,
    sourceConfig: value.sourceConfig,
    generationConfig: value.generationConfig,
    contentRefreshRequired: value.contentRefreshRequired === true,
  };
}

export function parseQuizSourceSnapshot(value: unknown): QuizSourceSnapshot | null {
  if (!isRecord(value)) return null;
  const capturedAt =
    typeof value.capturedAt === 'string' && !Number.isNaN(Date.parse(value.capturedAt))
      ? value.capturedAt
      : null;

  const legacyApiInputText = value.createdBy === 'api' ? parseLegacyApiInputText(value) : null;
  if (legacyApiInputText) {
    const kind = detectQuizSourceKind(legacyApiInputText);
    return {
      ...buildQuizSourceSnapshotFromText(
        kind,
        legacyApiInputText,
        capturedAt ?? '1970-01-01T00:00:00.000Z'
      ),
      generationOrigin: 'integrator_api',
    };
  }

  if (isRecord(value) && typeof value.source_text === 'string') {
    if (
      value.capturedAt !== undefined &&
      (typeof value.capturedAt !== 'string' || Number.isNaN(Date.parse(value.capturedAt)))
    ) {
      return null;
    }

    const sourceText = value.source_text;
    const inputSourceType =
      typeof value.input_source_type === 'string' ? value.input_source_type : '';
    const kind =
      inputSourceType === 'rss'
        ? 'rss_feed'
        : inputSourceType === 'url'
          ? 'article_url'
          : detectQuizSourceKind(sourceText);

    return buildQuizSourceSnapshotFromText(
      kind,
      sourceText,
      capturedAt ?? '1970-01-01T00:00:00.000Z'
    );
  }

  if (!isQuizSourceKind(value.kind)) {
    if (
      value.capturedAt !== undefined &&
      (typeof value.capturedAt !== 'string' || Number.isNaN(Date.parse(value.capturedAt)))
    ) {
      return null;
    }

    const config = isRecord(value.config) ? value.config : null;
    const inputText = typeof config?.inputText === 'string' ? config.inputText : null;
    const inputSourceType =
      typeof config?.inputSourceType === 'string'
        ? config.inputSourceType
        : typeof value.kind === 'string'
          ? value.kind
          : '';

    if (!inputText) return null;

    const kind =
      inputSourceType === 'rss'
        ? 'rss_feed'
        : inputSourceType === 'url'
          ? 'article_url'
          : detectQuizSourceKind(inputText);

    return buildQuizSourceSnapshotFromText(
      kind,
      inputText,
      capturedAt ?? '1970-01-01T00:00:00.000Z'
    );
  }

  if (!capturedAt) {
    return null;
  }

  const config = value.config;
  if (!isRecord(config)) return null;
  if (config.kind !== value.kind || !isQuizSourceKind(config.kind)) return null;
  const parsedConfig = parseQuizSourceConfig(config);
  if (!parsedConfig || parsedConfig.kind !== value.kind) return null;

  const inputText = typeof value.inputText === 'string' ? value.inputText : undefined;
  const generationOrigin =
    value.generationOrigin === 'integrator_api' ? value.generationOrigin : undefined;
  let questionSlots: QuizQuestionSlot[] | undefined;
  if (value.questionSlots !== undefined) {
    const parsedQuestionSlots = parseQuizQuestionSlots(value.questionSlots);
    if (!parsedQuestionSlots) return null;
    questionSlots = parsedQuestionSlots;
  }
  let questionSlotOutcomes: QuizQuestionSlotOutcome[] | undefined;
  if (value.questionSlotOutcomes !== undefined) {
    const parsedOutcomes = parseQuizQuestionSlotOutcomes(value.questionSlotOutcomes);
    if (!parsedOutcomes) return null;
    questionSlotOutcomes = parsedOutcomes;
  }
  return {
    kind: value.kind,
    config: parsedConfig,
    capturedAt,
    ...(inputText !== undefined ? { inputText } : {}),
    ...(questionSlots !== undefined ? { questionSlots } : {}),
    ...(questionSlotOutcomes !== undefined ? { questionSlotOutcomes } : {}),
    ...(generationOrigin !== undefined ? { generationOrigin } : {}),
  };
}
